import React, { useState, useEffect, useMemo } from 'react';
import { getInwardDeliveries, getInwardDelivery, getSuppliers, createInwardDelivery } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Eye, Loader2, PackageOpen, Trash2 } from 'lucide-react';

const UNITS = ['kg', 'litres', 'each', 'box', 'pallet', 'other'];
const ACCEPTS = ['accepted', 'rejected', 'partial'];

const emptyLine = { internal_item: '', supplier_product_code: '', supplier_lot: '', quantity: 0, unit: 'kg', accept_status: 'accepted', notes: '' };
const emptyDelivery = {
  supplier_id: '', delivery_date: new Date().toISOString().slice(0, 10),
  supplier_delivery_note: '', received_by: '', storage_location: '', notes: '',
  lines: [{ ...emptyLine }],
};

export default function InwardDeliveries() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(emptyDelivery);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([getInwardDeliveries(), getSuppliers()]);
      setItems(d.data); setSuppliers(s.data);
    } catch (e) { toast.error('Failed to load deliveries'); }
    finally { setLoading(false); }
  })(); }, []);

  const supName = (id) => suppliers.find(s => s.id === id)?.name || '—';
  const filtered = useMemo(() => items.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.reference || '').toLowerCase().includes(q)
      || (d.supplier_delivery_note || '').toLowerCase().includes(q)
      || (d.lines || []).some(ln => (ln.supplier_lot || '').toLowerCase().includes(q));
  }), [items, search]);

  const openNew = () => { setForm({ ...emptyDelivery, lines: [{ ...emptyLine }] }); setDialog(true); };
  const openView = async (d) => { try { const r = await getInwardDelivery(d.id); setViewing(r.data); } catch (e) { toast.error('Load failed'); } };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));
  const updateLine = (i, k, v) => setForm(f => ({ ...f, lines: f.lines.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln) }));
  const removeLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.supplier_id) { toast.error('Choose a supplier'); return; }
    if (!form.lines.length) { toast.error('Add at least one delivery line'); return; }
    setSaving(true);
    try {
      const payload = { ...form, lines: form.lines.map(ln => ({ ...ln, quantity: Number(ln.quantity) || 0 })) };
      await createInwardDelivery(payload);
      toast.success('Delivery recorded');
      setDialog(false);
      const d = await getInwardDeliveries(); setItems(d.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="inward-deliveries-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><PackageOpen className="w-5 h-5" />Inward Deliveries</h1>
          <p className="text-sm text-muted-foreground">Inwards · receipts of ingredients, packaging and other materials. Each line carries the supplier lot for full trace.</p>
        </div>
        <Button onClick={openNew} data-testid="btn-new-delivery"><Plus className="w-4 h-4 mr-1" />New delivery</Button>
      </div>

      <Card><CardContent className="pt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search reference, supplier note, lot…" className="pl-9" />
        </div>
      </CardContent></Card>

      {loading ? <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> :
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="text-left px-4 py-3">Reference</th><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Supplier</th><th className="text-left px-4 py-3">Supplier DN</th><th className="text-left px-4 py-3">Lines</th><th className="text-right px-4 py-3">Actions</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">No deliveries yet.</td></tr> :
                filtered.map(d => (
                  <tr key={d.id} className="border-t hover:bg-zinc-50">
                    <td className="px-4 py-3 font-mono text-xs">{d.reference}</td>
                    <td className="px-4 py-3">{d.delivery_date}</td>
                    <td className="px-4 py-3">{supName(d.supplier_id)}</td>
                    <td className="px-4 py-3 text-xs">{d.supplier_delivery_note || '—'}</td>
                    <td className="px-4 py-3"><Badge variant="outline">{(d.lines || []).length} lines</Badge></td>
                    <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => openView(d)}><Eye className="w-4 h-4" /></Button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </CardContent></Card>}

      {/* New delivery dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>New inward delivery</DialogTitle>
            <DialogDescription>Record what arrived today. Each line should carry the supplier's lot/batch — that's what gives you backward trace later.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-2">
            <div className="col-span-2"><Label>Supplier *</Label>
              <Select value={form.supplier_id} onValueChange={v => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choose supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Delivery date *</Label><Input type="date" value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} /></div>
            <div><Label>Supplier delivery note</Label><Input value={form.supplier_delivery_note} onChange={e => setForm({ ...form, supplier_delivery_note: e.target.value })} /></div>
            <div><Label>Received by</Label><Input value={form.received_by} onChange={e => setForm({ ...form, received_by: e.target.value })} /></div>
            <div><Label>Storage location</Label><Input value={form.storage_location} onChange={e => setForm({ ...form, storage_location: e.target.value })} /></div>
            <div className="col-span-3"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>

          <div className="mt-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm">Delivery lines</h3>
              <Button size="sm" variant="outline" onClick={addLine}><Plus className="w-4 h-4 mr-1" />Add line</Button>
            </div>
            <div className="space-y-2">
              {form.lines.map((ln, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 border rounded-md">
                  <div className="col-span-3"><Label className="text-xs">Internal item</Label><Input value={ln.internal_item} onChange={e => updateLine(i, 'internal_item', e.target.value)} placeholder="e.g. Cheese culture" /></div>
                  <div className="col-span-2"><Label className="text-xs">Supplier lot *</Label><Input value={ln.supplier_lot} onChange={e => updateLine(i, 'supplier_lot', e.target.value)} placeholder="e.g. MA4001-26A" /></div>
                  <div className="col-span-2"><Label className="text-xs">Sup. code</Label><Input value={ln.supplier_product_code} onChange={e => updateLine(i, 'supplier_product_code', e.target.value)} /></div>
                  <div className="col-span-2"><Label className="text-xs">Qty</Label><Input type="number" value={ln.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} /></div>
                  <div className="col-span-1"><Label className="text-xs">Unit</Label>
                    <Select value={ln.unit} onValueChange={v => updateLine(i, 'unit', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1"><Label className="text-xs">Status</Label>
                    <Select value={ln.accept_status} onValueChange={v => updateLine(i, 'accept_status', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{ACCEPTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 text-right">
                    {form.lines.length > 1 && <Button size="sm" variant="ghost" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save delivery</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          {viewing && <>
            <DialogHeader>
              <DialogTitle className="font-mono">{viewing.reference}</DialogTitle>
              <DialogDescription>{supName(viewing.supplier_id)} · {viewing.delivery_date} · DN {viewing.supplier_delivery_note || '—'}</DialogDescription>
            </DialogHeader>
            <table className="w-full text-sm border rounded-md">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr><th className="text-left px-3 py-2">Item</th><th className="text-left px-3 py-2">Supplier lot</th><th className="text-left px-3 py-2">Qty</th><th className="text-left px-3 py-2">Status</th></tr>
              </thead>
              <tbody>
                {(viewing.lines || []).map((ln, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2">
                      <div className="font-medium">{ln.internal_item}</div>
                      <div className="text-xs text-muted-foreground">{ln.supplier_product_code}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{ln.supplier_lot || '—'}</td>
                    <td className="px-3 py-2">{ln.quantity} {ln.unit}</td>
                    <td className="px-3 py-2"><Badge variant="outline">{ln.accept_status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <DialogFooter><Button variant="outline" onClick={() => setViewing(null)}>Close</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
